/**
 * Hook de branding whitelabel post-auth (ADR-014 F4-D + F4-E).
 *
 * F4-D introdujo el contrato `TenantBrand` consumido por componentes
 * post-auth (Layout header, futuros banners) que necesitan el nombre +
 * logo del integrador para reemplazar los hardcoded "Whatsfy". F4-E
 * persiste `brand` en `tenant.settings` y expone el resolved config en
 * `GET /auth/tenant-settings`. Este hook conecta ambos.
 *
 * Contrato:
 *   - Si la query `useTenantSettings()` aún no devolvió data (pre-login,
 *     loading inicial, sesión recién montada) → devuelve `null`. Cada
 *     caller aplica su propio fallback (`brand?.name ?? 'Whatsfy'`).
 *   - Cuando hay data → devuelve `{ name, logoUrl, primaryColor? }`
 *     directamente desde `config.brand`.
 *
 * Pre-auth (Login/Provision/EmbedError) NO debe usar este hook —
 * `useTenantSettings` requiere cookie JWT válida y devolvería 401.
 *
 * BUG-2 (DEEP-B-v3): el header del SPA hardcoded "Inventario Whatsfy"
 * ignoraba el setting persistido en DB porque F4-D dejó este hook como
 * stub `return null`. F4-E aterrizó la persistencia pero olvidó conectar
 * el frontend. Este file cierra el gap.
 */

import type { ResolvedTenantConfig } from './api';
import { useTenantSettings } from './queries';

export interface TenantBrand {
  name: string;
  logoUrl: string;
  primaryColor?: string;
}

/**
 * Pura — extraída para que el test no necesite QueryClientProvider.
 * Mapea el shape persistido del backend al shape consumido por la UI.
 *
 * Si el config viene sin `brand` (defensive — el backend siempre lo
 * resuelve con defaults, pero el tipo lo permite vacío durante migración
 * de schema) devuelve `null`. Los callers caen al default Whatsfy.
 */
export function selectBrand(
  config: ResolvedTenantConfig | undefined,
): TenantBrand | null {
  if (!config?.brand) return null;
  const { name, logoUrl, primaryColor } = config.brand;
  if (!name || !logoUrl) return null;
  return {
    name,
    logoUrl,
    ...(primaryColor ? { primaryColor } : {}),
  };
}

export function useTenantBrand(): TenantBrand | null {
  // useTenantSettings() devuelve undefined mientras carga; selectBrand
  // mapea undefined → null para que el caller aplique fallback.
  const { data } = useTenantSettings();
  return selectBrand(data?.config);
}
